import { QUERY_PARAMETERS, QUERY_PARAMETERS_READABLE } from 'Constants/QueryParameters';

const capitalizeParam = (param) => {
    let capitalizedParam = param;
    capitalizedParam = capitalizedParam.charAt(0).toUpperCase() + capitalizedParam.substring(1);
    capitalizedParam = capitalizedParam.replace('_', '-');
    return capitalizedParam;
};

const isOccupancyParam = param => (param.includes(QUERY_PARAMETERS.CHILD_ROOM)
|| param.includes(QUERY_PARAMETERS.ADULT_ROOM));

const transformOccupancyParams = (queryResult) => {
    const result = queryResult;

    const occupancyParams = Object.keys(queryResult).filter(key => isOccupancyParam(key));

    occupancyParams.forEach((param) => {
        result[capitalizeParam(param)] = queryResult[param];
        delete result[param];
    });

    return result;
};

const constructQueryObject = (query, state, id, isOffer, code, isMultiProperty = false, availableDaterange) => {
    const queryResult = query;
    queryResult[QUERY_PARAMETERS.CHANNEL_ID] = state.settings.data.channelId;
    queryResult[QUERY_PARAMETERS.CHECKIN] = state.search.data.checkIn;
    queryResult[QUERY_PARAMETERS.CHECKOUT] = state.search.data.checkOut;
    queryResult[QUERY_PARAMETERS.CURRENCY_CODE] = state.search.data.currency.code;
    queryResult[QUERY_PARAMETERS.LANGUAGE] = state.search.data.language;
    queryResult[QUERY_PARAMETERS.PROMOTION_CODE] = state.search.data.clientPromoCode;

    if (state.hotels.coupon || state.search.data?.coupon) {
        queryResult[QUERY_PARAMETERS.COUPON] = state.hotels.coupon || state.search.data.coupon;
    }

    const autocompleteType = state.hotels?.autocompleteData?.type;
    const sourceContext = state.settings.data.searchId;

    if (autocompleteType && autocompleteType === 'classification') {
        // Old autocomplete behavior ...
        queryResult[QUERY_PARAMETERS.AUTOCOMPLETE_VALUE] = state.hotels?.autocompleteData?.code;
        queryResult[QUERY_PARAMETERS.AUTOCOMPLETE_TITLE] = state.hotels?.autocompleteData?.value;
        queryResult[QUERY_PARAMETERS.AUTOCOMPLETE_VALUE_TYPE] = state.hotels?.autocompleteData?.type;
    }

    if (autocompleteType) {
        queryResult[QUERY_PARAMETERS.SEARCHBAR_TYPE] = state.hotels.autocompleteData.type;
        queryResult[QUERY_PARAMETERS.SEARCHBAR_CODE] = state.hotels.autocompleteData.code;
        queryResult[QUERY_PARAMETERS.SEARCHBAR_LABEL] = state.hotels.autocompleteData.value;
    }

    if (!isMultiProperty) {
        const { propertyCode } = state.settings.data;
        const { shouldUpgradeTier } = state.search.data;
        if (propertyCode) {
            queryResult[QUERY_PARAMETERS.PROPERTY_CODE] = propertyCode;
        }

        if (shouldUpgradeTier) {
            queryResult[QUERY_PARAMETERS.UPGRADE_TIER_MODAL_OPEN] = state.search.data.shouldUpgradeTier;
        }
    }

    queryResult[QUERY_PARAMETERS.WIDGET_ID] = id;
    queryResult[QUERY_PARAMETERS.LANGUAGE] = state.search.data.language;

    if (sourceContext) {
        queryResult[QUERY_PARAMETERS.SOURCE_CONTEXT] = sourceContext;
    }

    if (isOffer !== null) {
        queryResult[QUERY_PARAMETERS.WIDGET_SECTION] = isOffer ? 'offers' : 'rates';
    } else {
        queryResult[QUERY_PARAMETERS.WIDGET_SECTION] = 'searchbar';
    }

    if (code) {
        queryResult[isOffer ? QUERY_PARAMETERS.OFFER : QUERY_PARAMETERS.RATE] = code;
    }

    if (availableDaterange) {
        queryResult[QUERY_PARAMETERS.CHECKIN] = availableDaterange.start;
        queryResult[QUERY_PARAMETERS.CHECKOUT] = availableDaterange.end;
    }

    return queryResult;
};

const constructQueryReadableObject = (query, state, id, isOffer, code, isMultiProperty = false, availableDaterange) => {
    let queryResult = query;
    const isDestinationSelected = state.destinations && state.destinations.selected;

    queryResult[QUERY_PARAMETERS_READABLE.CHECKIN] = state.search.data.checkIn;
    queryResult[QUERY_PARAMETERS_READABLE.CHECKOUT] = state.search.data.checkOut;
    queryResult[QUERY_PARAMETERS_READABLE.CURRENCY_CODE] = state.search.data.currency.code;
    queryResult[QUERY_PARAMETERS_READABLE.PROMOTION_CODE] = state.search.data.promoCode;
    queryResult[QUERY_PARAMETERS_READABLE.LANGUAGE] = state.search.data.language;
    queryResult[QUERY_PARAMETERS_READABLE.PROMOTION_CODE] = state.search.data.clientPromoCode;

    let destination;

    if (isDestinationSelected) {
        const { type } = state.destinations.selected;

        if ((type === 'hotel')) {
            destination = state.destinations.selected.value;
        }

        if (type === 'country') {
            const { value:countryCode, englishValue:value } = state.destinations.selected;

            destination = { countryCode, value };
        }

        if (type === 'city') {
            const { countries } = state.destinations.data;

            const countryName = countries.find(country => (
                country.value === state.destinations.selected.code)).englishValue;

            destination = {
                countryCode : state.destinations.selected.code,
                countryName,
                city        : state.destinations.selected.value,
            };
        }

        queryResult[QUERY_PARAMETERS_READABLE.DESTINATION] = { type, destination };
    }

    if (query[QUERY_PARAMETERS.TOTAL_ROOMS]) {
        queryResult[QUERY_PARAMETERS_READABLE.TOTAL_ROOMS] = query[QUERY_PARAMETERS.TOTAL_ROOMS];
        delete queryResult[QUERY_PARAMETERS.TOTAL_ROOMS];
    }

    if (!isMultiProperty) {
        const { propertyCode } = state.settings.data;
        if (propertyCode) {
            queryResult[QUERY_PARAMETERS_READABLE.PROPERTY_CODE] = propertyCode;
        }
    }

    if (id) {
        queryResult[QUERY_PARAMETERS_READABLE.WIDGET_ID] = id;
    }

    if (isOffer) {
        queryResult[QUERY_PARAMETERS_READABLE.WIDGET_SECTION] = isOffer ? 'offers' : 'rates';
    } else {
        queryResult[QUERY_PARAMETERS_READABLE.WIDGET_SECTION] = 'searchbar';
    }

    if (code) {
        queryResult[isOffer ? QUERY_PARAMETERS_READABLE.OFFER : QUERY_PARAMETERS_READABLE.RATE] = code;
    }

    if (availableDaterange) {
        queryResult[QUERY_PARAMETERS.CHECKIN] = availableDaterange.start;
        queryResult[QUERY_PARAMETERS.CHECKOUT] = availableDaterange.end;
    }

    queryResult = transformOccupancyParams(queryResult);

    return queryResult;
};

/**
 * Transforms occupancy array to an object
 * with keys as number of room an value quantity of adults
 * and an array of children
 *
 * @param {*} occupancy
 */
export const transformOccupancyToUrl = (occupancy) => {
    const object = occupancy.reduce((acum, item, i) => {
        const roomIndex = i + 1;
        const currentConfig = { [`${QUERY_PARAMETERS.ADULT_ROOM}${roomIndex}`]: item && item.adults ? item.adults.toString() : '0' };
        const enabledAges = !item.children.some(age => age === -1);
        if (enabledAges) {
            const agesCounter = {};
            item.children.forEach((age) => {
                agesCounter[age] = agesCounter[age] ? agesCounter[age] + 1 : 1;
            });
            Object.keys(agesCounter)
                .forEach((age) => {
                    const param = `${QUERY_PARAMETERS.CHILD_ROOM}${roomIndex}_age${age}`;
                    currentConfig[param] = agesCounter && agesCounter[age] ? agesCounter[age].toString() : '0';
                });
        } else {
            currentConfig[`${QUERY_PARAMETERS.CHILD_ROOM}${roomIndex}`] = item && item.children ? item.children.length.toString() : '0';
        }
        return { ...acum, ...currentConfig };
    }, {});
    object[QUERY_PARAMETERS.TOTAL_ROOMS] = occupancy ? occupancy.length.toString() : '0';
    return object;
};

/**
 * Transforms all params to put into url query
 *
 * @param {*} params
 */
export const transformStateToParams = (id, state, code, isOffer = null, readable = false, availableDaterange = null) => {
    let query = { ...transformOccupancyToUrl(state.search.data.occupancy) };

    if (!readable) {
        query = constructQueryObject(query, state, id, isOffer, code, false, availableDaterange);
    } else {
        query = constructQueryReadableObject(query, state, id, isOffer, code, false, availableDaterange);
    }

    return query;
};

export const transformStateToMultiPropertyParams = (state, readable = false) => {
    let query = { ...transformOccupancyToUrl(state.search.data.occupancy) };

    if (!readable) {
        query = constructQueryObject(query, state, null, null, null, true);
    } else {
        query = constructQueryReadableObject(query, state, null, null, null, true);
    }

    return query;
};

export const getQueryString = (params, paramsToDelete = []) => {
    const queryParams = params;
    paramsToDelete.forEach((paramName) => {
        if (typeof queryParams[paramName] !== 'undefined') {
            delete queryParams[paramName];
        }
    });
    const queryString = Object.keys(queryParams).reduce((acum, key, i) => {
        if (queryParams[key] !== undefined) {
            const prefix = i === 0 ? '' : '&';
            return `${acum}${prefix}${key}=${queryParams[key]}`;
        }
        return acum;
    }, '');
    return queryString === '' ? '' : `?${queryString}`;
};
